import React from 'react';

import Axios from 'axios';
import _ from 'lodash';

import ls from 'local-storage';

import { eventsTypes } from '@configs/EVENT_TYPES';

import { GetTimestampNumeric } from '@libs/NwMoment';
import { GetForeColor } from '@libs/NwUtils';

const DEFAULT_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours
const LIST_CACHE_TTL_MS = {
    customFields: 0,
    publictags: 0,
    privatetags: 0,
    eventSubtypes: 5 * 60 * 1000,
    followUpLabels: 5 * 60 * 1000,
};

const getCacheTTL = (key) => {
    if (Object.prototype.hasOwnProperty.call(LIST_CACHE_TTL_MS, key)) {
        return LIST_CACHE_TTL_MS[key];
    }
    return DEFAULT_CACHE_TTL_MS;
};

const isLocalCacheFresh = (key, lastUpdate) => {
    const ttl = getCacheTTL(key);
    if (ttl === 0) {
        return false;
    }
    if (!Number.isFinite(lastUpdate) || lastUpdate <= 0) {
        return false;
    }
    return Date.now() - lastUpdate <= ttl;
};

const safeGetCacheEntry = (storageKey) => {
    try {
        return ls.get(storageKey);
    } catch (error) {
        ls.remove(storageKey);
        return null;
    }
};

const safeSetCacheEntry = (storageKey, value) => {
    try {
        if (value === undefined) {
            ls.remove(storageKey);
            return;
        }
        ls.set(storageKey, value);
    } catch (error) {
        ls.remove(storageKey);
    }
};

//Set the default Status at application start
const LIST_DEFAULT_STATE = {
    cachedListLoaded: false,
    cachedList: {
        countries: {
            url: '/countries',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        generalconfigurations: {
            url: '/configurations/general',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        cities: {
            url: '/cities',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        privatetags: {
            url: '/tags/private',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        publictags: {
            url: '/tags/public',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        users: {
            url: '/users',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        accountingGroups: {
            url: '/accountinggroups',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        transactionFamilies: {
            url: '/transactionfamilies',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        enumerators: {
            url: '/enumerators',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        taxmethods: {
            url: '/taxmethods/active',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        modelAgencyLabels: {
            url: '/modelagencylabels',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        followUpLabels: {
            url: '/followuplabels',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        eventSubtypes: {
            url: '/eventsubtypes',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        callsheetTypes: {
            url: '/callsheettypes',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        templates: {
            url: '/configurations/templates',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        layoutConfigurations: {
            url: '/configurations/layoutmodel',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        contactGatewayConfigurations: {
            url: '/configurations/contactgateway',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        connectionTypes: {
            url: '/connectiontypes',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        eventCategories: {
            url: '/eventcategories',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        projectCategories: {
            url: '/projectcategories',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        eventClientRoles: {
            url: '/eventclientroles',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        customFields: {
            url: '/customfields',
            isLoaded: false,
            fetchError: null,
            subKeys: [
                'Project',
                'Event',
                'ProjectModel',
                'EventModel',
                'Model',
                'Client',
                'Service',
                'Agency',
                'Invoice',
            ],
            items: {},
        },
        usageTypes: {
            url: '/UsageTypes',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        noteTypes: {
            url: '/NoteTypes',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        bankAccounts: {
            url: '/BankAccounts',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        paymentTerms: {
            url: '/PaymentTerms',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
        legalTypes: {
            url: '/legaltypes',
            isLoaded: false,
            fetchError: null,
            items: [],
        },
    },
};

const ListContext = React.createContext(LIST_DEFAULT_STATE);

const ListContextConsumer = ListContext.Consumer;

class ListContextProvider extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            ...LIST_DEFAULT_STATE,
            lastUpdate: Date.now(),
            LoadData: this.loadData,
            LoadUsers: this.loadUsers,
            UpdateCachedList: this.updateCachedList,
            UpdateCachedListwithStatic: this.updateCachedListwithStatic,
            AdjustTagUsageCount: this.adjustTagUsageCount,
            GetCountry: this.getCountry,
            GetCity: this.getCity,
            GetUserName: this.getUserName,
            GetUserNameShort: this.getUserNameShort,
            GetCategoryName: this.getCategoryName,
            GetTagName: this.getTagName,
            GetEventClientRole: this.getEventClientRole,
            GetFamilyName: this.getFamilyName,
            GetTransactionLabelName: this.getTransactionLabelName,
        };
    }

    loadData = async () => {
        return new Promise((resolve, reject) => {
            this.loadCachedLists()
                .then((newCachedLists) => {
                    const ful = newCachedLists.find((l) => {
                        return l.key.toLowerCase() === 'followuplabels';
                    });
                    const boo = newCachedLists.find((l) => {
                        return l.key.toLowerCase() === 'eventsubtypes';
                    });
                    const cas = boo.data.items.filter((s) => {
                        return s.TypeName.toLowerCase() === 'casting';
                    });
                    const job = boo.data.items.filter((s) => {
                        return s.TypeName.toLowerCase() === 'job';
                    });
                    const ext = boo.data.items.filter((s) => {
                        return s.TypeName.toLowerCase() === 'ext';
                    });

                    const eventTypesItems = [...eventsTypes];
                    eventTypesItems.find((x) => x.id === 10).subTypes = cas.map((i) => {
                        return _.pick(i, ['Code', 'Name', 'ForeColor', 'BackColor', 'Disabled', 'Default']);
                    });
                    eventTypesItems.find((x) => x.id === 20).subTypes = job.map((i) => {
                        return _.pick(i, [
                            'Code',
                            'Name',
                            'ForeColor',
                            'BackColor',
                            'OptionForeColor',
                            'OptionBackColor',
                            'FirstOptionBackColor',
                            'FirstOptionForeColor',
                            'SecondOptionBackColor',
                            'SecondOptionForeColor',
                            'ThirdOptionBackColor',
                            'ThirdOptionForeColor',
                            'Disabled',
                            'Default',
                        ]);
                    });
                    eventTypesItems.find((x) => x.id === 30).subTypes = ext.map((i) => {
                        return _.pick(i, [
                            'Code',
                            'Name',
                            'ForeColor',
                            'BackColor',
                            'OptionForeColor',
                            'OptionBackColor',
                            'FirstOptionBackColor',
                            'FirstOptionForeColor',
                            'SecondOptionBackColor',
                            'SecondOptionForeColor',
                            'ThirdOptionBackColor',
                            'ThirdOptionForeColor',
                            'Disabled',
                            'Default',
                        ]);
                    });
                    eventTypesItems.find((x) => x.id === 110).subTypes = ful.data.items.map((i) => ({
                        ID: i.ID,
                        Code: i.Label,
                        Name: i.Label,
                        ForeColor: null,
                        BackColor: null,
                    }));

                    newCachedLists.push({
                        key: 'eventTypes',
                        data: {
                            isLoaded: true,
                            fetchError: null,
                            items: eventTypesItems,
                        },
                    });

                    this.setState(() => ({
                        cachedListLoaded: true,
                        cachedList: Object.assign({}, ...newCachedLists.map((d) => ({ [d.key]: d.data }))),
                    }));
                    resolve();
                })
                .catch((error) => {
                    //TODO: manage errors
                    reject(error);
                });
        });
    };

    //Methods to Load Initial Cached Lists;
    loadCachedLists = () => {
        //wait for all the lists to be fetched
        return Promise.all(
            Object.keys(this.state.cachedList)
                .filter((key) => this.state.cachedList[key] && this.state.cachedList[key].url)
                .map((key) =>
                    //Iterate through cachedlists and return a promise for each of them
                    this.loadSingleCachedList(key)
                )
        );
    };

    loadUsers = async () => {
        const users = await Axios.get('/users');
        this.setState({
            cachedList: {
                ...this.state.cachedList,
                users: {
                    ...this.state.cachedList.users,
                    items: [...users],
                },
            },
        });
    };

    //Load a single Cached List
    loadSingleCachedList = async (key) => {
        const listState = this.state.cachedList[key];
        if (!listState || !listState.url) {
            throw {
                key,
                data: {
                    ...listState,
                    items: listState && listState.items ? listState.items : [],
                    isLoaded: false,
                    fetchError: new Error(`Cached list '${key}' is not refreshable`),
                },
            };
        }

        const cl_local = safeGetCacheEntry(`nw_cachedlist_${key}`);
        const cl_lastupdate_local = Number(safeGetCacheEntry(`nw_cachedlist_${key}_lastupdate`));
        const hasLocalCache = cl_local !== undefined && cl_local !== null;
        const canUseLocalCache = hasLocalCache && isLocalCacheFresh(key, cl_lastupdate_local);

        if (canUseLocalCache) {
            let clist = { ...listState };
            clist.items = cl_local;
            clist.isLoaded = true;
            clist.fetchError = null;
            return { key, data: clist };
        }

        try {
            let clist = { ...listState };
            if (key === 'customFields') {
                const customFieldsByType = {};
                for (const subKey of listState.subKeys) {
                    const subCustomFields = await Axios.get(`${listState.url}/${subKey}`);
                    customFieldsByType[subKey] = subCustomFields;
                }
                clist.items = customFieldsByType;
            } else {
                const data = await Axios.get(listState.url);
                if (key === 'followUpLabels') {
                    clist.items = data.map((followupLabel) => {
                        return {
                            ...followupLabel,
                            BackColor: `#${followupLabel.color}`,
                            ForeColor: GetForeColor(`#${followupLabel.color}`),
                        };
                    });
                } else {
                    clist.items = data;
                }
            }
            clist.isLoaded = true;
            clist.fetchError = null;

            safeSetCacheEntry(`nw_cachedlist_${key}`, clist.items);
            safeSetCacheEntry(`nw_cachedlist_${key}_lastupdate`, GetTimestampNumeric());

            return { key, data: clist };
        } catch (error) {
            if (hasLocalCache) {
                let clist = { ...listState };
                clist.items = cl_local;
                clist.isLoaded = true;
                clist.fetchError = error;
                return { key, data: clist };
            }

            let clist = { ...listState };
            clist.items = key === 'customFields' ? {} : [];
            clist.isLoaded = false;
            clist.fetchError = error;
            throw { key, data: clist };
        }
    };

    updateCachedList = async (key) => {
        if (!this.state.cachedList[key] || !this.state.cachedList[key].url) {
            return null;
        }
        ls.remove(`nw_cachedlist_${key}`);
        ls.remove(`nw_cachedlist_${key}_lastupdate`);
        try {
            const result = await this.loadSingleCachedList(key);

            await new Promise((resolve) => {
                this.setState((prevState) => {
                    // Update the specific cached list
                    const updatedCachedList = {
                        ...prevState.cachedList,
                        [key]: result.data,
                    };

                    // If we're updating eventSubtypes, we need to rebuild eventTypes
                    if (key === 'eventSubtypes') {
                        const eventSubtypes = result.data.items;
                        const followUpLabels = updatedCachedList.followUpLabels.items || [];

                        const cas = eventSubtypes.filter((s) => s.TypeName.toLowerCase() === 'casting');
                        const job = eventSubtypes.filter((s) => s.TypeName.toLowerCase() === 'job');
                        const ext = eventSubtypes.filter((s) => s.TypeName.toLowerCase() === 'ext');
                        const eventTypesItems = [...eventsTypes];
                        eventTypesItems.find((x) => x.id === 10).subTypes = cas.map((i) => {
                            return _.pick(i, ['Code', 'Name', 'ForeColor', 'BackColor', 'Disabled', 'Default']);
                        });
                        eventTypesItems.find((x) => x.id === 20).subTypes = job.map((i) => {
                            return _.pick(i, [
                                'Code',
                                'Name',
                                'ForeColor',
                                'BackColor',
                                'OptionForeColor',
                                'OptionBackColor',
                                'FirstOptionBackColor',
                                'FirstOptionForeColor',
                                'SecondOptionBackColor',
                                'SecondOptionForeColor',
                                'ThirdOptionBackColor',
                                'ThirdOptionForeColor',
                                'Disabled',
                                'Default',
                            ]);
                        });
                        eventTypesItems.find((x) => x.id === 30).subTypes = ext.map((i) => {
                            return _.pick(i, [
                                'Code',
                                'Name',
                                'ForeColor',
                                'BackColor',
                                'OptionForeColor',
                                'OptionBackColor',
                                'FirstOptionBackColor',
                                'FirstOptionForeColor',
                                'SecondOptionBackColor',
                                'SecondOptionForeColor',
                                'ThirdOptionBackColor',
                                'ThirdOptionForeColor',
                                'Disabled',
                                'Default',
                            ]);
                        });
                        eventTypesItems.find((x) => x.id === 110).subTypes = followUpLabels.map((i) => ({
                            ID: i.ID,
                            Code: i.Label,
                            Name: i.Label,
                            ForeColor: null,
                            BackColor: null,
                        }));

                        updatedCachedList.eventTypes = {
                            isLoaded: true,
                            fetchError: null,
                            items: eventTypesItems,
                        };
                    }

                    // Force a complete re-render by creating a new state object with timestamp
                    return {
                        cachedList: updatedCachedList,
                        lastUpdate: Date.now(),
                    };
                }, resolve);
            });

            return result.data;
        } catch (error) {
            console.error('ERROR', error);
            throw error;
        }
    };

    updateCachedListwithStatic = (key, data) => {
        this.setState({
            cachedList: {
                ...this.state.cachedList,
                [key]: {
                    ...this.state.cachedList[key],
                    items: data,
                },
            },
        });
        ls.remove(`nw_cachedlist_${key}`);
        safeSetCacheEntry(`nw_cachedlist_${key}`, data);
        safeSetCacheEntry(`nw_cachedlist_${key}_lastupdate`, GetTimestampNumeric());
    };

    // Dopo un'associazione riuscita: aggiorna UsageCount nella lista pubblica o privata.
    // delta puo' essere > 1 (bulk). Non usare UpdateCachedList: cancellerebbe la cache prima del GET.
    adjustTagUsageCount = (tagId, delta) => {
        const normalizedId = Number(tagId);
        if (!normalizedId || !delta) {
            return;
        }

        const patchList = (key) => {
            const current = this.state.cachedList[key];
            if (!current || !current.items) {
                return null;
            }
            const index = current.items.findIndex((tag) => Number(tag.ID) === normalizedId);
            if (index === -1) {
                return null;
            }
            return current.items.map((tag) => {
                if (Number(tag.ID) !== normalizedId) {
                    return tag;
                }
                const currentCount = Number.isFinite(tag.UsageCount) ? tag.UsageCount : 0;
                return {
                    ...tag,
                    UsageCount: Math.max(0, currentCount + delta),
                };
            });
        };

        let key = 'publictags';
        let nextItems = patchList('publictags');
        if (!nextItems) {
            key = 'privatetags';
            nextItems = patchList('privatetags');
        }
        if (!nextItems) {
            return;
        }

        this.setState({
            cachedList: {
                ...this.state.cachedList,
                [key]: {
                    ...this.state.cachedList[key],
                    items: nextItems,
                },
            },
            lastUpdate: Date.now(),
        });
        ls.remove(`nw_cachedlist_${key}`);
        safeSetCacheEntry(`nw_cachedlist_${key}`, nextItems);
        safeSetCacheEntry(`nw_cachedlist_${key}_lastupdate`, GetTimestampNumeric());
    };

    getCountry = (code) => {
        return this.state.cachedList.countries.items.find((country) => country.Code === code);
    };

    getCity = (cityName) => {
        return this.state.cachedList.cities.items.find((city) => city.Name === cityName);
    };

    getUserName = (userID) => {
        const user = this.state.cachedList.users.items.find((user) => user.ID === userID);
        let un = '';
        if (user) {
            un += user.Name;
            if (user.Surname) {
                un += ' ' + user.Surname;
            }
        }
        return un;
    };

    getUserNameShort = (userID) => {
        const user = this.state.cachedList.users.items.find((user) => user.ID === userID);
        let un = '';
        if (user) {
            un += user.Name;
            if (user.Surname && user.Surname.trim().length > 0) {
                un += ' ' + user.Surname.substring(0, 1) + '.';
            }
        }
        return un;
    };

    getCategoryName = (categoryID) => {
        return this.state.cachedList.eventCategories.items.find((category) => category.ID === categoryID).Name;
    };

    getTagName = (tagId) => {
        return [...this.state.cachedList.publictags.items, ...this.state.cachedList.privatetags.items].find(
            (tag) => tag.ID === tagId
        ).Name;
    };

    getEventClientRole = (roleId) => {
        const role = this.state.cachedList.eventClientRoles.items.find((role) => role.ID === roleId);
        return role && role.Name;
    };

    getFamilyName = (code) => {
        const family = this.state.cachedList.transactionFamilies.items.find((tf) => tf.Code === code);
        if (family) {
            return family.Name;
        }
        return '';
    };

    getTransactionLabelName = (transactionFamily, transactionLabelID) => {
        const family = this.state.cachedList.transactionFamilies.items.find((tf) => tf.Code === transactionFamily);
        if (family && family.TransactionLabels) {
            const label = family.TransactionLabels.find((tl) => tl.ID === transactionLabelID);
            if (label) {
                return label.Label;
            }
        }
        return '';
    };

    render() {
        return <ListContext.Provider value={this.state}>{this.props.children}</ListContext.Provider>;
    }
}

export { ListContext, ListContextProvider, ListContextConsumer };
